import { useMutation, useQueryClient } from '@tanstack/react-query'
import { saveGoalsData } from '../data/goalsApi.js'
import { useHistoryStore } from '../store/historyStore.js'

/**
 * useUpdateGoal — mutation hook for changing a goal's fields.
 *
 * Input shape:
 *   { pillarName, goalIndex, subgoalIndex?, field, value }
 *
 *   - pillarName: "Revenue", "Growth", etc. (matches data[pi].pillar)
 *   - goalIndex: index into pillar.goals
 *   - subgoalIndex: (optional) index into goal.subgoals; if provided,
 *     we're editing a sub-goal rather than the goal itself
 *   - field: "current", "target", "update", "goal", etc.
 *   - value: new value
 *
 * Pattern:
 *   1. onMutate → write optimistically to the cache (instant UI update)
 *   2. mutationFn → POST the new array to /goals on the worker
 *   3. onError → roll back to the previous cache
 *   4. onSettled → invalidate to fetch the authoritative server state
 *
 * This is the canonical TanStack Query optimistic-update pattern.
 * Every future mutation (comments, weekly updates, etc.) follows the same shape.
 */
export function useUpdateGoal() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ pillarName, goalIndex, subgoalIndex, field, value }) => {
      // Start from the current cache
      const current = queryClient.getQueryData(['goals'])
      if (!current) throw new Error('Goals data not loaded yet')

      // Build a fresh copy with the one field updated
      const next = structuredClone(current)
      const pi = next.findIndex((p) => p.pillar === pillarName)
      if (pi < 0) throw new Error(`Pillar "${pillarName}" not found`)
      const goal = next[pi].goals?.[goalIndex]
      if (!goal) throw new Error(`Goal ${goalIndex} not found in ${pillarName}`)

      if (typeof subgoalIndex === 'number') {
        const sub = goal.subgoals?.[subgoalIndex]
        if (!sub) throw new Error(`Sub-goal ${subgoalIndex} not found`)
        sub[field] = value
      } else {
        goal[field] = value
      }

      // Send to the server — worker persists to KV
      await saveGoalsData(next)
      return next
    },

    // Optimistic: apply the same change to the local cache immediately
    onMutate: async ({ pillarName, goalIndex, subgoalIndex, field, value }) => {
      await queryClient.cancelQueries({ queryKey: ['goals'] })
      const previous = queryClient.getQueryData(['goals'])

      // Push a snapshot to the undo stack (capture both goals + meta so
      // multi-field undos can restore things like attribution alongside
      // the goal-value change they made)
      useHistoryStore
        .getState()
        .pushSnapshot(
          `Edit ${field} on ${pillarName}`,
          previous,
          queryClient.getQueryData(['meta'])
        )

      queryClient.setQueryData(['goals'], (old) => {
        if (!old) return old
        const next = structuredClone(old)
        const pi = next.findIndex((p) => p.pillar === pillarName)
        if (pi < 0) return old
        const goal = next[pi].goals?.[goalIndex]
        if (!goal) return old
        if (typeof subgoalIndex === 'number') {
          const sub = goal.subgoals?.[subgoalIndex]
          if (sub) sub[field] = value
        } else {
          goal[field] = value
        }
        return next
      })

      return { previous }
    },

    // If the server rejects, roll back the optimistic update
    onError: (err, vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['goals'], ctx.previous)
      }
    },

    // Whether success or failure, refetch so we end up with truth-from-server
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] })
    },
  })
}
